import type { AddShortlistItemFlowSpec } from '@elara/domain/flows';

import { buildPreviewMarkdownLink, isPreviewType } from '@elara/domain/schemas/preview-schemas';

import type { ToolBadgeSpec } from '../types.js';

type AddShortlistItemOutput = AddShortlistItemFlowSpec['OutputType'];

type ShortlistItemToolOutput = AddShortlistItemOutput | { success: boolean; item: AddShortlistItemOutput };

type ShortlistItemFromTool = AddShortlistItemOutput['item'];

interface ShortlistGroupToolOutput {
  group?: { name?: string | null };
}

function shortlistItemLabel(output: unknown, isActive: boolean, presentTemplate: string, pastTemplate: string): string {
  const raw = output as ShortlistItemToolOutput | undefined;
  const result = raw?.item as ShortlistItemFromTool | undefined;
  const title = result?.title ?? (isActive ? 'something' : 'Something');
  const type = result?.type;
  const referenceId = result?.referenceId;
  const linkedTitle =
    referenceId != null && isPreviewType(type) ? buildPreviewMarkdownLink(title, type, referenceId) : title;
  return isActive ? presentTemplate.replace('ITEM_NAME', title) : pastTemplate.replace('ITEM_NAME', linkedTitle);
}

function shortlistGroupLabel(
  output: unknown,
  isActive: boolean,
  presentTemplate: string,
  pastTemplate: string,
): string {
  const raw = output as ShortlistGroupToolOutput | undefined;
  const name = raw?.group?.name ?? (isActive ? 'group' : 'A');
  return (isActive ? presentTemplate : pastTemplate).replace('GROUP_NAME', name);
}

/* -------- Item operations (render linked item title in past-tense) -------- */

export const addToShortlistSpec: ToolBadgeSpec = {
  toolName: 'addToShortlist',
  icon: 'heart_plus',
  labelMarkdown: true,
  label: (tc, active) =>
    shortlistItemLabel(tc.output, active, 'Adding ITEM_NAME to shortlist', 'ITEM_NAME added to shortlist'),
  activitySummary: {
    label: (_, active) => (active ? 'Adding items to your shortlist' : 'Added items to your shortlist'),
  },
};

export const removeFromShortlistSpec: ToolBadgeSpec = {
  toolName: 'removeFromShortlist',
  icon: 'heart_minus',
  labelMarkdown: true,
  label: (tc, active) =>
    shortlistItemLabel(tc.output, active, 'Removing ITEM_NAME from shortlist', 'ITEM_NAME removed from shortlist'),
  activitySummary: {
    label: (_, active) => (active ? 'Removing items from your shortlist' : 'Removed items from your shortlist'),
  },
};

export const updateShortlistItemSpec: ToolBadgeSpec = {
  toolName: 'updateShortlistItem',
  icon: 'heart_check',
  labelMarkdown: true,
  label: (tc, active) => shortlistItemLabel(tc.output, active, 'Updating shortlist', 'ITEM_NAME updated in shortlist'),
  activitySummary: { label: (_, active) => (active ? 'Updating your shortlist' : 'Updated your shortlist') },
};

/* -------- Group operations (render group name in the label) -------- */

export const addShortlistGroupSpec: ToolBadgeSpec = {
  toolName: 'addShortlistGroup',
  icon: 'heart_plus',
  labelMarkdown: true,
  label: (tc, active) =>
    shortlistGroupLabel(tc.output, active, 'Creating GROUP_NAME shortlist', 'GROUP_NAME shortlist created'),
  activitySummary: { label: (_, active) => (active ? 'Creating a shortlist' : 'Created a shortlist') },
};

export const removeShortlistGroupSpec: ToolBadgeSpec = {
  toolName: 'removeShortlistGroup',
  icon: 'heart_minus',
  labelMarkdown: true,
  label: (tc, active) =>
    shortlistGroupLabel(tc.output, active, 'Removing GROUP_NAME shortlist', 'GROUP_NAME shortlist removed'),
  activitySummary: { label: (_, active) => (active ? 'Removing a shortlist' : 'Removed a shortlist') },
};

export const updateShortlistGroupSpec: ToolBadgeSpec = {
  toolName: 'updateShortlistGroup',
  icon: 'heart_check',
  labelMarkdown: true,
  label: (tc, active) =>
    shortlistGroupLabel(tc.output, active, 'Updating GROUP_NAME shortlist', 'GROUP_NAME shortlist updated'),
  activitySummary: { label: (_, active) => (active ? 'Updating your shortlists' : 'Updated your shortlists') },
};

/* -------- Reorder operations (no item/group name needed) -------- */

export const reorderShortlistItemsSpec: ToolBadgeSpec = {
  toolName: 'reorderShortlistItems',
  icon: 'heart_check',
  label: (_, active) => (active ? 'Reordering shortlist items' : 'Reordered shortlist items'),
};

export const reorderShortlistGroupsSpec: ToolBadgeSpec = {
  toolName: 'reorderShortlistGroups',
  icon: 'heart_check',
  label: (_, active) => (active ? 'Reordering shortlists' : 'Reordered shortlists'),
};
